import { useEffect, useRef } from 'react';

const asbestosTypes = [
  { name: '백석면', en: 'Chrysotile', color: 'bg-stone-100', desc: '가장 광범위하게 사용된 종류로, 건설 자재에 많이 포함됨' },
  { name: '갈석면', en: 'Amosite', color: 'bg-amber-50', desc: '단열재, 방음재 등에 주로 사용된 각섬석계 석면' },
  { name: '청석면', en: 'Crocidolite', color: 'bg-stone-100', desc: '가장 위험한 종류로 발암성이 매우 높음' },
  { name: '직섬석', en: 'Anthophyllite', color: 'bg-amber-50', desc: '내열성이 강하여 고온 환경의 단열재에 사용' },
  { name: '투각섬석', en: 'Tremolite', color: 'bg-stone-100', desc: '주로 활석이나 버미큘라이트에 불순물로 포함됨' },
  { name: '양기석', en: 'Actinolite', color: 'bg-amber-50', desc: '녹색 계열로 내화재 및 단열재에 혼합 사용' },
];

const buildingMaterials = [
  { icon: 'ri-building-2-line', name: '석면 슬레이트', desc: '지붕 및 외벽 마감재' },
  { icon: 'ri-layout-grid-line', name: '텍스·천장재', desc: '실내 천장 마감 타일' },
  { icon: 'ri-fire-line', name: '방화·단열재', desc: '방화문, 단열 패널류' },
  { icon: 'ri-checkbox-blank-line', name: '바닥재·타일', desc: '비닐 바닥재, 비닐장판, 접착제' },
  { icon: 'ri-pipe-line', name: '배관 보온재', desc: '파이프 단열 감싸기 재료' },
  { icon: 'ri-tools-line', name: '가스켓·패킹', desc: '기계 설비의 밀폐 부품' },
  { icon: 'ri-door-open-line', name: '내화도료', desc: '방화 구획 내 마감 처리' },
  { icon: 'ri-construction-line', name: '석고보드류', desc: '경량 벽체 및 천장 보드' },
];

export default function AsbestosOverview() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="overview" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* 섹션 헤더 — PC만 표시 */}
        <div className="section-fade-in mb-16 hidden md:block">
          <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
            About Asbestos
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-green-950 leading-tight whitespace-nowrap">
              석면의 개요
            </h2>
            <p className="text-stone-500 text-base max-w-md leading-relaxed">
              석면은 자연계에 존재하는 섬유상 규산염 광물로, 인체에 치명적인&nbsp; 1급 발암물질입니다.
              정확한 이해가 안전한 관리의 시작입니다.
            </p>
          </div>
        </div>

        {/* 모바일 섹션 헤더 */}
        <div className="section-fade-in mb-8 md:hidden">
          <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-4 py-1.5 rounded-full mb-3 tracking-widest uppercase">
            About Asbestos
          </span>
          <h2 className="text-2xl font-bold text-green-950 leading-tight">석면의 개요</h2>
        </div>

        {/* 석면이란 + 전자현미경 이미지 그리드 */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* 석면이란 카드 — 모바일에서 전체 너비 */}
          <div className="section-fade-in bg-green-950 rounded-3xl p-8 md:p-10 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-800/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-alert-line text-amber-400 text-xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-amber-400">석면이란?</h3>
              <p className="text-white/80 leading-relaxed mb-6">
                석면(Asbestos)은 자연계에서 산출되는 섬유상 규산염 광물의 총칭입니다.
                내열성, 내화학성이 뛰어나 과거 건축 자재로 널리 사용되었으나,
                흡입 시 석면폐, 폐암, 악성중피종을 유발하는 <strong className="text-amber-300">1급 발암물질</strong>로
                현재는 사용이 금지되어 있습니다.
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: '석면폐', icon: 'ri-lungs-line' },
                  { label: '폐암', icon: 'ri-heart-pulse-line' },
                  { label: '악성중피종', icon: 'ri-virus-line' },
                ].map((item) => (
                  <div key={item.label} className="bg-white/10 rounded-xl p-3 text-center">
                    <i className={`${item.icon} text-amber-400 text-lg block mb-1`}></i>
                    <span className="text-xs text-white/70">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 전자현미경 이미지 — PC만 표시 */}
          <div className="section-fade-in rounded-3xl overflow-hidden relative min-h-[300px] hidden md:block">
            <img
              src="https://public.readdy.ai/ai/img_res/013f07c2b9155c806d5a104638fe16b0.jpg"
              alt="각섬석 석면 전자현미경 이미지"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-950/80 via-green-950/20 to-transparent flex flex-col justify-end p-8">
              <span className="text-amber-400 text-sm font-semibold mb-2 tracking-wide">MICROSCOPIC VIEW</span>
              <p className="text-white text-xl font-bold">전자현미경으로 본 석면</p>
              <p className="text-white/70 text-sm mt-2">직경 3μm 이하의 미세 섬유가 폐에 침착되어 심각한 질환을 유발합니다</p>
            </div>
          </div>

          {/* 전자현미경 이미지 — 모바일 전용 (배경 이미지 형태) */}
          <div className="section-fade-in rounded-3xl overflow-hidden relative min-h-[200px] md:hidden">
            <img
              src="https://readdy.ai/api/search-image?query=scanning%20electron%20microscopy%20SEM%20close-up%20image%20chrysotile%20asbestos%20fibers%20bundle%20white%20bright%20fibers%20dark%20background%20scientific%20photograph%20highly%20detailed%20magnification%20electron%20microscope%20view%20mineral%20fiber%20structure%20research%20laboratory&width=800&height=400&seq=asb-sem-mobile-009&orientation=landscape"
              alt="전자현미경으로 본 석면 섬유"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-950/80 via-green-950/30 to-transparent flex flex-col justify-end p-6">
              <span className="text-amber-400 text-xs font-semibold mb-1 tracking-wide">MICROSCOPIC VIEW</span>
              <p className="text-white text-base font-bold">전자현미경으로 본 석면 섬유</p>
              <p className="text-white/70 text-xs mt-1">직경 3μm 이하의 미세 섬유 — 1급 발암물질</p>
            </div>
          </div>
        </div>

        {/* 석면의 종류 */}
        <div className="section-fade-in mb-6">
          <h3 className="text-2xl font-bold text-green-950 mb-2">석면의 종류</h3>
          <p className="text-stone-500 text-sm hidden md:block">국내 법적으로 관리 대상인 6가지 석면 종류</p>
        </div>

        {/* PC: 이름+설명, 모바일: 이름만 */}
        <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4 mb-16">
          {asbestosTypes.map((type, i) => (
            <div
              key={type.name}
              className={`section-fade-in ${type.color} rounded-2xl border border-stone-200/60 hover:shadow-sm transition-all duration-200 cursor-default`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* PC 카드 */}
              <div className="hidden md:block p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-lg font-bold text-green-950">{type.name}</div>
                    <div className="text-xs text-stone-400 font-medium">{type.en}</div>
                  </div>
                  <span className="text-xs bg-green-800/10 text-green-800 px-2 py-1 rounded-full font-medium">
                    0{i + 1}
                  </span>
                </div>
                <p className="text-sm text-stone-600 leading-relaxed">{type.desc}</p>
              </div>
              {/* 모바일 카드 — 이름만 */}
              <div className="md:hidden p-4 text-center">
                <div className="text-sm font-bold text-green-950 mb-0.5">{type.name}</div>
                <div className="text-xs text-stone-400">{type.en}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 대표적 석면함유 건축자재 */}
        <div className="section-fade-in mb-6">
          <h3 className="text-2xl font-bold text-green-950 mb-2">대표적 석면함유 건축자재</h3>
          <p className="text-stone-500 text-sm hidden md:block">건물 해체·리모델링 전 반드시 확인이 필요한 자재 유형</p>
        </div>

        {/* PC: 3열, 모바일: 2x4 (2열 4행) */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {buildingMaterials.map((mat, i) => (
            <div
              key={mat.name}
              className="section-fade-in flex items-start gap-3 md:gap-4 bg-stone-50 rounded-2xl p-4 md:p-5 border border-stone-100 hover:border-green-200 hover:bg-green-50/30 transition-all duration-200 cursor-default"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-green-800 rounded-xl flex-shrink-0">
                <i className={`${mat.icon} text-white text-sm md:text-base`}></i>
              </div>
              <div>
                <div className="font-semibold text-green-950 text-xs md:text-sm">{mat.name}</div>
                <div className="text-xs text-stone-500 mt-0.5 hidden md:block">{mat.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
